import React, { useState, useEffect } from "react";
import axios from "axios";
import { DeclarationText } from "./DeclarationText";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { AlertCircle, Eye, EyeOff, User, Mail, Lock, Shield, LogIn, UserPlus, LogOut, Calendar, BarChart3, Euro, CreditCard, Download, Library, Heart, Link2, X, Edit, Receipt } from "lucide-react";
import { toast } from "sonner";

// Login Form Component
export const LoginForm = ({ isOpen, onClose, onLogin, loginForm, setLoginForm, onForgotPassword }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    try {
      await onLogin(loginForm);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Incorrect email or password';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5" />
            Login to Associazione Culturale Europea - APS Member's Portal
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="username">Email Address</Label>
            <div className="relative">
              <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="username"
                type="email"
                placeholder="Enter your email"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className="pl-10"
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="pl-10 pr-10"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1 h-8 w-8"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              id="remember_me"
              type="checkbox"
              checked={loginForm.remember_me}
              onChange={(e) => setLoginForm({...loginForm, remember_me: e.target.checked})}
              className="rounded border-slate-300"
            />
            <Label htmlFor="remember_me" className="text-sm font-normal">
              Remember me (7 days)
            </Label>
          </div>
          
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="login-error-message">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1 bg-slate-700 hover:bg-slate-800" disabled={loading}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
          
          {/* Forgot Password Link */}
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                onForgotPassword();
              }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Forgot your password?
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Registration Form Component
export const RegisterForm = ({ 
  isOpen, 
  onClose, 
  onRegister, 
  registerForm, 
  setRegisterForm, 
  nationalities 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // Multi-step form
  const [fieldErrors, setFieldErrors] = useState({}); // Track field-specific errors
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState(null); // Store Stripe checkout URL
  const [isCheckingEmail, setIsCheckingEmail] = useState(false); // Track email availability check
  const [emailCheckTimer, setEmailCheckTimer] = useState(null); // Debounce timer for email check
  const [selectedMembershipYear, setSelectedMembershipYear] = useState(null); // Selected membership year (Sept+ feature)
  // Cross-year signup config comes from GET /settings/membership-year-options.
  // Each option = {year, fee, expires_on, configured}. Legacy fallbacks below
  // are only used if the endpoint is unreachable so registration still works.
  const [yearOptions, setYearOptions] = useState(null); // null = loading, [] = none available
  const currentYear = new Date().getFullYear();
  const canSelectNextYear = Array.isArray(yearOptions) && yearOptions.length > 1;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    axios
      .get("/settings/membership-year-options")
      .then((res) => {
        if (!cancelled) setYearOptions(res.data?.options || []);
      })
      .catch(() => {
        // Fallback: assume only the current year is offered.
        if (!cancelled) setYearOptions([{ year: currentYear, fee: null, configured: false, expires_on: `${currentYear}-12-31` }]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentYear]);

  // Step 3 declaration text is editable in Global Settings.
  const [declarationText, setDeclarationText] = useState("");
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    axios.get("/settings/membership-declaration").then((res) => setDeclarationText(res.data?.text || "")).catch(() => setDeclarationText("The membership declaration could not be loaded. Please close this window and try again."));
  }, [isOpen]);
  // Year/fee shown in the declaration: chosen year, else all offered years (e.g. "2026 / 2027").
  const declOptions = (yearOptions || []).filter((o) => !selectedMembershipYear || o.year === selectedMembershipYear);
  const declYear = declOptions.length ? declOptions.map((o) => o.year).join(" / ") : currentYear;
  const declFee = [...new Set(declOptions.map((o) => (o.fee == null ? null : Number(o.fee).toFixed(2).replace(/\.00$/, ""))).filter(Boolean))].join(" / ");

  // Debug: Log nationalities
  console.log('RegisterForm nationalities:', nationalities);

  // Fallback nationalities if none provided
  const availableNationalities = nationalities && nationalities.length > 0 ? nationalities : [
    "Austrian", "Belgian", "Bulgarian", "Croatian", "Cypriot", "Czech", 
    "Danish", "Dutch", "Estonian", "Finnish", "French", "German", 
    "Greek", "Hungarian", "Irish", "Italian", "Latvian", "Lithuanian", 
    "Luxembourgish", "Maltese", "Polish", "Portuguese", "Romanian", 
    "Slovak", "Slovenian", "Spanish", "Swedish", "Other"
  ];

  // Field validation functions
  const validateStep1 = () => {
    const errors = {};
    
    // First name 1 validation
    if (!registerForm.first_name_1 || registerForm.first_name_1.trim().length < 2) {
      errors.first_name_1 = "First name 1 is required (minimum 2 characters)";
    }
    
    // Last name validation
    if (!registerForm.last_name || registerForm.last_name.trim().length < 2) {
      errors.last_name = "Last name is required (minimum 2 characters)";
    }
    
    // Email validation
    if (!registerForm.email) {
      errors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerForm.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    // Mobile validation
    if (!registerForm.mobile) {
      errors.mobile = "Mobile number is required";
    } else if (!/^[+]?[\d\s-()]{10,}$/.test(registerForm.mobile)) {
      errors.mobile = "Please enter a valid mobile number with country code";
    }
    
    // Password validation
    if (!registerForm.password) {
      errors.password = "Password is required";
    } else if (registerForm.password.length < 8) {
      errors.password = "Password must be at least 8 characters long";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(registerForm.password)) {
      errors.password = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    }
    
    // Last year member validation
    if (registerForm.last_year_member === null || registerForm.last_year_member === undefined) {
      errors.last_year_member = "Please select if you were a member last year";
    }
    
    // Member category validation  
    if (!registerForm.member_category) {
      errors.member_category = "Member category is required";
    }
    
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    
    // Birth date validation
    if (!registerForm.birth_date) {
      errors.birth_date = "Birth date is required";
    } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(registerForm.birth_date)) {
      errors.birth_date = "Birth date must be in DD/MM/YYYY format";
    }
    
    // Birth place validation
    if (!registerForm.birth_place || registerForm.birth_place.trim().length < 2) {
      errors.birth_place = "Birth place is required (minimum 2 characters)";
    }
    
    // Nationality validation
    if (!registerForm.nationality) {
      errors.nationality = "Nationality is required";
    }
    
    // Address validation
    if (!registerForm.address || registerForm.address.trim().length < 5) {
      errors.address = "Complete address is required (minimum 5 characters)";
    }
    
    // Postal code validation
    if (!registerForm.postal_code) {
      errors.postal_code = "Postal code is required";
    } else if (!/^\d{5}$/.test(registerForm.postal_code)) {
      errors.postal_code = "Postal code must be exactly 5 digits";
    }
    
    // City validation
    if (!registerForm.city || registerForm.city.trim().length < 2) {
      errors.city = "City is required (minimum 2 characters)";
    }
    
    // Province validation (now optional)
    if (registerForm.province && !/^[A-Z]{2}$/.test(registerForm.province)) {
      errors.province = "Province must be exactly 2 capital letters (e.g., MI)";
    }
    
    // Country validation
    if (!registerForm.country) {
      errors.country = "Country is required";
    }
    
    // Fiscal code validation
    if (!registerForm.fiscal_code) {
      errors.fiscal_code = "Fiscal code is required";
    } else if (registerForm.fiscal_code.length < 11) {
      errors.fiscal_code = "Fiscal code must be at least 11 characters";
    } else if (registerForm.fiscal_code.length > 16) {
      errors.fiscal_code = "Fiscal code must not exceed 16 characters";
    }
    
    return errors;
  };

  const clearFieldError = (fieldName) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Check email availability with debouncing
  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) {
      return; // Don't check if email is empty or invalid format
    }

    try {
      setIsCheckingEmail(true);
      const response = await axios.get(`/auth/check-email?email=${encodeURIComponent(email)}`);
      
      if (!response.data.available) {
        setFieldErrors(prev => ({ 
          ...prev, 
          email: "This email is already registered. Please use a different email or try logging in." 
        }));
      } else {
        // Clear email error if it was set
        if (fieldErrors.email) {
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
        }
      }
    } catch (error) {
      console.error('Error checking email availability:', error);
      // Don't show error to user, just log it
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setRegisterForm({ ...registerForm, [fieldName]: value });
    
    // Clear error for this field when user starts typing
    if (fieldErrors[fieldName]) {
      setFieldErrors({ ...fieldErrors, [fieldName]: null });
    }

    // Special handling for email field - check availability with debouncing
    if (fieldName === 'email') {
      // Clear existing timer
      if (emailCheckTimer) {
        clearTimeout(emailCheckTimer);
      }
      
      // Set new timer to check email after user stops typing (500ms delay)
      const timer = setTimeout(() => {
        checkEmailAvailability(value);
      }, 500);
      
      setEmailCheckTimer(timer);
    }
  };

  // Error message component
  const FieldError = ({ fieldName }) => {
    if (!fieldErrors[fieldName]) return null;
    return (
      <div className="text-red-600 text-sm mt-1 mb-2 flex items-center gap-1">
        <AlertCircle className="h-4 w-4" />
        {fieldErrors[fieldName]}
      </div>
    );
  };

  const handleSubmit = async (e, overrideYear = null) => {
    e.preventDefault();
    
    // Only handle Step 4 (payment step) in this function
    if (step !== 4) {
      return;
    }
    
    setLoading(true);
    setIsValidating(true);
    
    // Resolve the year to charge: prefer the explicit override passed in from
    // the year card's onClick (React batches setState, so reading the state
    // here would still be the previous value on the first click).
    const yearToUse = overrideYear || selectedMembershipYear;
    if (overrideYear && overrideYear !== selectedMembershipYear) {
      setSelectedMembershipYear(overrideYear);
    }

    try {
      console.log('🔄 Creating membership payment session for year:', yearToUse);
      
      // Create Stripe checkout session with email from registration form
      const response = await axios.post('membership/payment/create-checkout', {
        origin_url: window.location.origin,
        email: registerForm.email,  // Pass email from registration step 1
        selected_membership_year: yearToUse || undefined,
      });
      
      console.log('💳 Payment session created:', {
        session_id: response.data.session_id,
        checkout_url: response.data.checkout_url?.substring(0, 50) + '...'
      });
      
      if (response.data.checkout_url && response.data.session_id) {
        // CRITICAL FIX: Create pending registration BEFORE payment redirect
        console.log('🔄 Creating pending registration with session ID:', response.data.session_id);
        
        try {
          // Register user with the payment session ID to create pending registration
          const registrationData = {
            ...registerForm,
            payment_session_id: response.data.session_id,
            selected_membership_year: yearToUse // Pass selected year to backend
          };
          
          console.log('📝 Registration data:', {
            email: registrationData.email,
            session_id: registrationData.payment_session_id,
            first_name: registrationData.first_name,
            last_name: registrationData.last_name
          });
          
          const registrationResult = await onRegister(registrationData);
          
          console.log('✅ Pending registration created successfully:', registrationResult);
          
          // Verify registration was actually created
          if (!registrationResult || registrationResult.error) {
            throw new Error(`Registration failed: ${registrationResult?.error || 'Unknown error'}`);
          }
          
          // Store minimal data for return verification
          sessionStorage.setItem('pendingRegistration', JSON.stringify({
            email: registerForm.email,
            session_id: response.data.session_id,
            timestamp: new Date().toISOString()
          }));
          
          console.log('✅ Payment session ready. Showing payment button...');
          
          // Store the checkout URL and show a button instead of auto-redirecting
          // This is more reliable across all browsers (especially Safari)
          setCheckoutUrl(response.data.checkout_url);
          setLoading(false);
          setIsValidating(false);
          toast.success("Registration saved! Click the button below to complete payment.", { duration: 5000 });
          
        } catch (registrationError) {
          console.error('❌ CRITICAL: Failed to create pending registration:', registrationError);
          console.error('❌ Registration error details:', {
            message: registrationError.message,
            status: registrationError.response?.status,
            statusText: registrationError.response?.statusText,
            data: registrationError.response?.data,
            stack: registrationError.stack
          });
          
          // Show loading state to prevent user confusion
          setLoading(false);
          setIsValidating(false);
          
          // Handle specific backend validation errors
          if (registrationError.response?.data?.detail) {
            const errorMessage = registrationError.response.data.detail;
            
            if (errorMessage.includes('email already exists') || errorMessage.includes('Email') && errorMessage.includes('already exists')) {
              setFieldErrors({ email: "This email address is already registered" });
              setError("This email is already registered. Please use a different email or try logging in.");
              // Don't reset to step 1, stay on step 4 and show error
              toast.error(
                <div>
                  <p><strong>Email already registered!</strong></p>
                  <p>This email address already has an account. Please:</p>
                  <ul style={{listStyle: 'disc', paddingLeft: '20px', marginTop: '5px'}}>
                    <li>Try logging in instead</li>
                    <li>Use "Forgot Password" if needed</li>
                    <li>Use a different email address</li>
                  </ul>
                </div>,
                { duration: 8000 }
              );
            } else if (errorMessage.includes('fiscal code') || errorMessage.includes('fiscal_code')) {
              setFieldErrors({ fiscal_code: "This fiscal code is already registered" });
              setError("Fiscal code already registered. Please check your fiscal code or contact support.");
              toast.error("Fiscal code already registered. Please check your fiscal code or contact support.");
            } else {
              setError("Registration error: " + errorMessage);
              toast.error("Registration validation failed: " + errorMessage);
            }
          } else {
            setError("Failed to create registration. Please try again or contact support.");
            toast.error("Failed to create registration. Please try again.");
          }
          
          // CRITICAL: Don't redirect to Stripe if registration failed
          console.error('🚫 BLOCKING Stripe redirect due to registration failure');
          
          // Don't reset to step 1, stay on step 4 so user can see the error
          return;
        }
      } else {
        console.error('❌ No checkout URL in response:', response.data);
        toast.error('Payment system error: No checkout URL received. Please contact support.');
      }
      
    } catch (error) {
      console.error('❌ Payment session creation failed:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // Show user-friendly error message
      let errorMessage = 'Payment system error. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error creating payment. Please try again or contact support.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Handle specific backend validation errors
      if (error.response?.data?.detail) {
        const backendError = error.response.data.detail;
        
        // Parse backend errors and map to specific fields
        if (backendError.includes('email already exists') || backendError.includes('User with this email')) {
          setFieldErrors({ email: "This email address is already registered" });
          setStep(1); // Go back to step 1
          toast.error("Email address already registered");
          setLoading(false);
          setIsValidating(false);
          return;
        } else if (backendError.includes('fiscal code') || backendError.includes('fiscal_code')) {
          setFieldErrors({ fiscal_code: "This fiscal code is already registered" });
          setStep(2); // Go back to step 2
          toast.error("Fiscal code already registered");
          setLoading(false);
          setIsValidating(false);
          return;
        }
      }
      
      // Show specific error message
      const errorMsg = error.response?.data?.detail || error.message || "Failed to create payment session";
      toast.error(`Payment Error: ${errorMsg}`);
      setError(`Error: ${errorMsg}`);
      setLoading(false);
      setIsValidating(false);
    }
  };

  const nextStep = () => {
    let errors = {};
    
    if (step === 1) {
      errors = validateStep1();
    } else if (step === 2) {
      errors = validateStep2();
    } else if (step === 3) {
      errors = declarationAccepted ? {} : { declaration: "Please tick the box to accept the membership declaration." };
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Show general error for first field with error
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }
    
    setFieldErrors({});
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{backgroundColor: step === 1 || step === 2 ? '#D5F0EF' : 'white'}}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Become an ACE member — Step {step} of 4
          </DialogTitle>
          <p className="text-sm text-slate-600 mt-2">Pick your membership year and pay securely — the card is issued instantly.</p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reg_first_name_1">First Name 1 *</Label>
                  <Input
                    id="reg_first_name_1"
                    placeholder="e.g. Maria"
                    value={registerForm.first_name_1}
                    onChange={(e) => handleFieldChange('first_name_1', e.target.value)}
                    className={fieldErrors.first_name_1 ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                    required
                  />
                  <FieldError fieldName="first_name_1" />
                </div>
                <div>
                  <Label htmlFor="reg_first_name_2">First Name 2</Label>
                  <Input
                    id="reg_first_name_2"
                    value={registerForm.first_name_2}
                    onChange={(e) => handleFieldChange('first_name_2', e.target.value)}
                    className={fieldErrors.first_name_2 ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                  />
                  <FieldError fieldName="first_name_2" />
                </div>
              </div>

              <div>
                <Label htmlFor="reg_last_name">Last Name *</Label>
                <Input
                  id="reg_last_name"
                  placeholder="e.g. BIANCHI"
                  value={registerForm.last_name}
                  onChange={(e) => handleFieldChange('last_name', e.target.value)}
                  className={fieldErrors.last_name ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                  required
                />
                <FieldError fieldName="last_name" />
              </div>

              <div>
                <Label htmlFor="reg_email">Email Address *</Label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="reg_email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    className={fieldErrors.email ? "pl-10 pr-10 border-red-500 focus:border-red-500 bg-white" : "pl-10 pr-10 bg-white"}
                    placeholder="Enter your email address"
                    required
                  />
                  {isCheckingEmail && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                <FieldError fieldName="email" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reg_mobile">Mobile *</Label>
                  <Input
                    id="reg_mobile"
                    type="tel"
                    value={registerForm.mobile}
                    onChange={(e) => handleFieldChange('mobile', e.target.value)}
                    className={fieldErrors.mobile ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                    placeholder="+39 xxx xxx xxxx"
                    required
                  />
                  <div className="text-xs text-slate-600 mt-1">
                    Phone number will be used to contact you if necessary
                  </div>
                  <FieldError fieldName="mobile" />
                </div>
                <div>
                  <Label htmlFor="reg_landline">Landline</Label>
                  <Input
                    id="reg_landline"
                    type="tel"
                    value={registerForm.landline}
                    onChange={(e) => handleFieldChange('landline', e.target.value)}
                    className={fieldErrors.landline ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                    placeholder="E.g. 0332 XXXX"
                  />
                  <FieldError fieldName="landline" />
                </div>
              </div>

              <div>
                <Label htmlFor="reg_password">Password *</Label>
                <div className="relative">
                  <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="reg_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters with uppercase, lowercase, and number"
                    value={registerForm.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    className={fieldErrors.password ? "pl-10 pr-10 border-red-500 focus:border-red-500 bg-white" : "pl-10 pr-10 bg-white"}
                    minLength="8"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-8 w-8"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <FieldError fieldName="password" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reg_last_year_member">Last Year Member *</Label>
                  <Select 
                    value={registerForm.last_year_member.toString()} 
                    onValueChange={(value) => handleFieldChange('last_year_member', value === 'true')}
                  >
                    <SelectTrigger className={fieldErrors.last_year_member ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}>
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                  <FieldError fieldName="last_year_member" />
                </div>
                <div>
                  <Label htmlFor="reg_member_category">Member Category *</Label>
                  <Select 
                    value={registerForm.member_category} 
                    onValueChange={(value) => handleFieldChange('member_category', value)}
                  >
                    <SelectTrigger className={fieldErrors.member_category ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="JRC Internal">
                        <div className="flex flex-col">
                          <span className="font-semibold">JRC Internal</span>
                          <span className="text-xs text-slate-600">Employees and retirees of the European Commission at the JRC Ispra, their spouses and children</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="JRC External">
                        <div className="flex flex-col">
                          <span className="font-semibold">JRC External</span>
                          <span className="text-xs text-slate-600">All others, with a limit of 40% of the total number of members</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FieldError fieldName="member_category" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reg_birth_date">Birth Date * <span className="text-slate-500 font-normal">(DD/MM/YYYY, e.g. 12/05/1984)</span></Label>
                  <div className="relative">
                    <Input
                      id="reg_birth_date"
                      value={registerForm.birth_date}
                      onChange={(e) => {
                        let val = e.target.value.replace(/[^\d/]/g, '');
                        const digits = val.replace(/\//g, '');
                        if (digits.length <= 8) {
                          let formatted = '';
                          for (let i = 0; i < digits.length; i++) {
                            if (i === 2 || i === 4) formatted += '/';
                            formatted += digits[i];
                          }
                          handleFieldChange('birth_date', formatted);
                        }
                      }}
                      maxLength={10}
                      className={`font-mono tracking-wider ${fieldErrors.birth_date ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}`}
                      required
                    />
                    {/* Ghost mask — shows DD/MM/YYYY with the "/" separators always visible */}
                    <div
                      className="pointer-events-none absolute inset-y-0 left-3 flex items-center font-mono tracking-wider text-slate-300 select-none"
                      aria-hidden="true"
                    >
                      <span className="invisible">{registerForm.birth_date}</span>
                      <span>{'DD/MM/YYYY'.slice(registerForm.birth_date.length)}</span>
                    </div>
                  </div>
                  <FieldError fieldName="birth_date" />
                </div>
                <div>
                  <Label htmlFor="reg_birth_place">Birth Place *</Label>
                  <Input
                    id="reg_birth_place"
                    value={registerForm.birth_place}
                    onChange={(e) => handleFieldChange('birth_place', e.target.value)}
                    className={fieldErrors.birth_place ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                    required
                  />
                  <FieldError fieldName="birth_place" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reg_nationality">Nationality *</Label>
                  <Select 
                    value={registerForm.nationality} 
                    onValueChange={(value) => handleFieldChange('nationality', value)}
                  >
                    <SelectTrigger className={fieldErrors.nationality ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}>
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableNationalities && availableNationalities.length > 0 ? (
                        availableNationalities.map((nationality) => (
                          <SelectItem key={nationality} value={nationality}>
                            {nationality}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="loading" disabled>Loading nationalities...</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FieldError fieldName="nationality" />
                </div>
                <div>
                  <Label htmlFor="reg_other_nationality">Other Nationality</Label>
                  <Input
                    id="reg_other_nationality"
                    value={registerForm.other_nationality}
                    onChange={(e) => handleFieldChange('other_nationality', e.target.value)}
                    disabled={registerForm.nationality !== 'Other'}
                    className={registerForm.nationality === 'Other' ? 'bg-white' : ''}
                    placeholder={registerForm.nationality === 'Other' ? 'Please specify' : 'Only if Other selected'}
                  />
                  <FieldError fieldName="other_nationality" />
                </div>
              </div>

              <div>
                <Label htmlFor="reg_address">Address *</Label>
                <Input
                  id="reg_address"
                  value={registerForm.address}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  className={fieldErrors.address ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                  placeholder="Via Roma 123, Apartment 4B"
                  required
                />
                <FieldError fieldName="address" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="reg_postal_code">Postal Code *</Label>
                  <Input
                    id="reg_postal_code"
                    maxLength="5"
                    pattern="[0-9]{5}"
                    value={registerForm.postal_code}
                    onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                    className={fieldErrors.postal_code ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                    required
                  />
                  <FieldError fieldName="postal_code" />
                </div>
                <div>
                  <Label htmlFor="reg_city">City *</Label>
                  <Input
                    id="reg_city"
                    value={registerForm.city}
                    onChange={(e) => handleFieldChange('city', e.target.value)}
                    className={fieldErrors.city ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                    required
                  />
                  <FieldError fieldName="city" />
                </div>
                <div>
                  <Label htmlFor="reg_province">Province</Label>
                  <p className="text-xs text-slate-600 mb-1">Insert XX if not applicable</p>
                  <Input
                    id="reg_province"
                    maxLength="2"
                    placeholder="e.g. MI"
                    value={registerForm.province}
                    onChange={(e) => handleFieldChange('province', e.target.value.toUpperCase())}
                    className={fieldErrors.province ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                  />
                  <FieldError fieldName="province" />
                </div>
              </div>

              <div>
                <Label htmlFor="reg_country">Country *</Label>
                <Select 
                  value={registerForm.country} 
                  onValueChange={(value) => handleFieldChange('country', value)}
                >
                  <SelectTrigger className={fieldErrors.country ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Italy">Italy</SelectItem>
                    <SelectItem value="Austria">Austria</SelectItem>
                    <SelectItem value="Belgium">Belgium</SelectItem>
                    <SelectItem value="Bulgaria">Bulgaria</SelectItem>
                    <SelectItem value="Croatia">Croatia</SelectItem>
                    <SelectItem value="Cyprus">Cyprus</SelectItem>
                    <SelectItem value="Czech Republic">Czech Republic</SelectItem>
                    <SelectItem value="Denmark">Denmark</SelectItem>
                    <SelectItem value="Estonia">Estonia</SelectItem>
                    <SelectItem value="Finland">Finland</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="Greece">Greece</SelectItem>
                    <SelectItem value="Hungary">Hungary</SelectItem>
                    <SelectItem value="Ireland">Ireland</SelectItem>
                    <SelectItem value="Latvia">Latvia</SelectItem>
                    <SelectItem value="Lithuania">Lithuania</SelectItem>
                    <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                    <SelectItem value="Malta">Malta</SelectItem>
                    <SelectItem value="Netherlands">Netherlands</SelectItem>
                    <SelectItem value="Poland">Poland</SelectItem>
                    <SelectItem value="Portugal">Portugal</SelectItem>
                    <SelectItem value="Romania">Romania</SelectItem>
                    <SelectItem value="Slovakia">Slovakia</SelectItem>
                    <SelectItem value="Slovenia">Slovenia</SelectItem>
                    <SelectItem value="Spain">Spain</SelectItem>
                    <SelectItem value="Sweden">Sweden</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FieldError fieldName="country" />
              </div>

              <div>
                <Label htmlFor="reg_fiscal_code">Fiscal Code (16 Characters) *</Label>
                <Input
                  id="reg_fiscal_code"
                  maxLength="16"
                  value={registerForm.fiscal_code}
                  onChange={(e) => handleFieldChange('fiscal_code', e.target.value.toUpperCase())}
                  className={fieldErrors.fiscal_code ? "border-red-500 focus:border-red-500 bg-white" : "bg-white"}
                  placeholder="Enter your fiscal code"
                  required
                />
                <FieldError fieldName="fiscal_code" />
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <AlertCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Membership Request Declaration</h3>
                    <p className="text-sm text-gray-600">Please read and accept the membership terms</p>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                  <DeclarationText text={declarationText} year={declYear} fee={declFee} />
                </div>
                
                <div className="mt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={declarationAccepted}
                      onChange={(e) => { setDeclarationAccepted(e.target.checked); if (e.target.checked) clearFieldError('declaration'); }}
                      data-testid="declaration-accept-checkbox"
                      required
                    />
                    <span className="text-sm text-gray-700">
                      <strong>I have read, understood, and accept all the above declarations and conditions for ACE membership {declYear}.</strong>
                    </span>
                  </label>
                  {fieldErrors.declaration && (
                    <p className="mt-2 text-sm font-medium text-red-600" data-testid="declaration-error">{fieldErrors.declaration}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              {/* Year Selection (config-driven cross-year window) */}
              {canSelectNextYear && !checkoutUrl && !loading && (
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-6 rounded-lg border-2 border-emerald-200">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Membership Year</h3>
                    <p className="text-sm text-gray-600">
                      The next membership year is now open. Pick which year you'd like to join.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {yearOptions.map((opt, idx) => {
                      const isCurrent = opt.year === currentYear;
                      const disabled = !opt.configured || loading;
                      return (
                        <button
                          key={opt.year}
                          type="button"
                          onClick={async (e) => {
                            if (!opt.configured) return;
                            setSelectedMembershipYear(opt.year);
                            // Pass the chosen year explicitly so we don't
                            // race React's batched state update.
                            await handleSubmit(
                              new Event('submit', { bubbles: true, cancelable: true }),
                              opt.year,
                            );
                          }}
                          disabled={disabled}
                          data-testid={`membership-year-${opt.year}`}
                          className={`${
                            isCurrent
                              ? "bg-white hover:bg-blue-50 border-blue-300"
                              : "bg-gradient-to-br from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 border-emerald-400"
                          } border-2 rounded-lg p-6 text-left transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className={`${isCurrent ? "bg-blue-100" : "bg-emerald-100"} p-2 rounded-full`}>
                              <Euro className={`h-5 w-5 ${isCurrent ? "text-blue-600" : "text-emerald-600"}`} />
                            </div>
                            <span
                              className={`text-sm font-medium ${
                                isCurrent ? "text-blue-600 bg-blue-100" : "text-emerald-700 bg-emerald-100"
                              } px-2 py-1 rounded`}
                            >
                              {isCurrent ? "Current Year" : "Early Access"}
                            </span>
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">Membership {opt.year}</h4>
                          {opt.configured ? (
                            <p className={`text-2xl font-bold mb-3 ${isCurrent ? "text-blue-600" : "text-emerald-600"}`}>
                              €{Number(opt.fee).toFixed(2)}
                            </p>
                          ) : (
                            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mb-3">
                              Not yet configured — contact admin
                            </p>
                          )}
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>✓ Valid: 01/01/{opt.year} - 31/12/{opt.year}</p>
                            {isCurrent ? (
                              <>
                                <p>✓ Immediate access to all events</p>
                                <p>✓ Digital membership card</p>
                              </>
                            ) : (
                              <>
                                <p>✓ Book events for {opt.year} now!</p>
                                <p>✓ Digital membership card</p>
                              </>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Standard Payment View (single year available OR after year selected) */}
              {(!canSelectNextYear || selectedMembershipYear) && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-100 p-2 rounded-full">
                      <Euro className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Complete Your ACE Membership</h3>
                      <p className="text-sm text-gray-600">Secure payment processing with Stripe</p>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-700">
                        ACE Membership {selectedMembershipYear || (yearOptions?.[0]?.year) || currentYear}
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        {(() => {
                          const y = selectedMembershipYear || (yearOptions?.[0]?.year) || currentYear;
                          const opt = (yearOptions || []).find((o) => o.year === y);
                          return opt?.configured ? `€${Number(opt.fee).toFixed(2)}` : "—";
                        })()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>✓ Full year membership (January - December {selectedMembershipYear || (yearOptions?.[0]?.year) || currentYear})</p>
                      <p>✓ Access to exclusive cultural events</p>
                      <p>✓ Digital membership card</p>
                      <p>✓ Member benefits and discounts</p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-800 flex items-center gap-2">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      Secure payment powered by Stripe. Your payment information is encrypted and secure.
                    </p>
                  </div>
                </div>
              )}
              
              {checkoutUrl && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 mb-3 flex items-center gap-2">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <strong>Registration details saved!</strong> Click the button below to complete your payment.
                  </p>
                  <button
                    type="button"
                    onClick={() => window.location.href = checkoutUrl}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Euro className="h-5 w-5" />
                    {(() => {
                      const opt = (yearOptions || []).find((o) => o.year === selectedMembershipYear);
                      return opt?.configured
                        ? `Continue to Secure Payment (€${Number(opt.fee).toFixed(2)})`
                        : "Continue to Secure Payment";
                    })()}
                  </button>
                  <p className="text-xs text-gray-600 text-center mt-2">
                    You will be redirected to Stripe's secure payment page
                  </p>
                </div>
              )}
              
              {loading && !checkoutUrl && (
                <div className="text-center py-4">
                  <div className="inline-flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    Preparing payment session...
                  </div>
                </div>
              )}
              
              {error && !loading && !checkoutUrl && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm text-red-800 flex items-center gap-2">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </p>
                  <button
                    type="button"
                    onClick={() => setError("")}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}
          
          <Separator />
          
          <div className="flex justify-between gap-2 pt-4">
            <div>
              {(step === 2 || step === 3 || step === 4) && (
                <Button type="button" variant="outline" onClick={prevStep}>
                  Previous
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              
              {step === 1 ? (
                <Button type="button" onClick={nextStep} className="bg-slate-700 hover:bg-slate-800">
                  Next
                </Button>
              ) : step === 2 ? (
                <Button type="button" onClick={nextStep} className="bg-blue-600 hover:bg-blue-700">
                  Next
                </Button>
              ) : step === 3 ? (
                <Button type="button" onClick={nextStep} className="bg-blue-600 hover:bg-blue-700">
                  Proceed to Payment
                </Button>
              ) : !checkoutUrl && !canSelectNextYear ? (
                <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={loading}>
                  {loading ? "Preparing payment..." : "Prepare Payment Session"}
                </Button>
              ) : null}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// User Menu Component
export const UserMenu = ({ currentUser, nationalities = [], onLogout, onPromoteUser, onViewBookings, onViewLoans, onViewDashboard, onChangePassword, onExportMyData, onDeleteMyAccount, onViewPaymentHistory }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  const handleDownloadMembershipCard = async () => {
    try {
      const response = await axios.get(`membership/card/${currentUser.id}`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Use filename from backend's Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${currentUser.last_name}_ACE_Membership_Card_${currentUser.membership_year || new Date().getFullYear()}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Membership card downloaded successfully!');
    } catch (error) {
      console.error('Failed to download membership card:', error);
      if (error.response?.status === 403) {
        toast.error('Membership card is only available for users with successful payment');
      } else {
        toast.error('Failed to download membership card. Please try again.');
      }
    }
  };

  // Helper function to format membership expiry - use membership_year to show 31/12/YEAR
  const formatMembershipExpiry = () => {
    if (currentUser?.membership_year) {
      return `31/12/${currentUser.membership_year}`;
    }
    return null;
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        className="flex items-center gap-2 px-2 sm:px-3"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {currentUser?.role === 'Administrator' ? (
            <Shield className="h-4 w-4 text-amber-600" />
          ) : (
            <User className="h-4 w-4 text-slate-600" />
          )}
          {/* Full text block on ≥sm only — mobile keeps the icon and lets the dropdown surface the details. */}
          <div className="hidden sm:block text-left">
            <div className="text-sm font-medium">
              {currentUser?.first_name_1 || currentUser?.first_name} {currentUser?.last_name}
            </div>
            <div className="text-xs text-slate-500">
              {currentUser?.role === 'Administrator' ? 'Administrator' : 'Regular User'}
            </div>
            {currentUser?.is_membership_active && currentUser?.membership_year && (
              <div className="text-xs text-green-600 font-medium">
                Active Member Until {formatMembershipExpiry()}
              </div>
            )}
            {!currentUser?.is_membership_active && currentUser?.role !== 'Administrator' && (
              <div className="text-xs text-red-500 font-medium">
                Membership Expired
              </div>
            )}
          </div>
        </div>
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
          <div className="p-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              {currentUser?.role === 'Administrator' ? (
                <Badge variant="default" className="bg-amber-100 text-amber-800 border-amber-300">
                  <Shield className="h-3 w-3 mr-1" />
                  Administrator
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <User className="h-3 w-3 mr-1" />
                  Regular User
                </Badge>
              )}
              {currentUser?.is_verified && (
                <Badge variant="outline" className="text-green-600 border-green-300">
                  Verified
                </Badge>
              )}
            </div>
            <div className="mt-2 text-sm text-slate-600">
              <div>{currentUser?.email}</div>
              {currentUser?.member_number && (
                <div className="text-xs text-blue-600 font-medium mt-1" data-testid="member-number-display">
                  Member No. {currentUser.member_number}
                </div>
              )}
              <div className="text-xs text-slate-500 mt-1">
                {currentUser?.member_category}
              </div>
            </div>
          </div>
          
          <div className="p-2 space-y-1">
            {/* Admin Dashboard button */}
            {currentUser?.role === 'Administrator' && onViewDashboard && (
              <Button
                variant="ghost"
                className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                onClick={() => {
                  setIsOpen(false);
                  onViewDashboard();
                }}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Admin Dashboard
              </Button>
            )}
            
            {/* Regular users: My Bookings button */}
            {currentUser?.role !== 'Administrator' && onViewBookings && (
              <Button
                variant="ghost"
                className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  setIsOpen(false);
                  onViewBookings();
                }}
              >
                <Calendar className="h-4 w-4 mr-2" />
                My Bookings
              </Button>
            )}

            {/* Regular users: My Loans button */}
            {currentUser?.role !== 'Administrator' && onViewLoans && (
              <Button
                variant="ghost"
                className="w-full justify-start text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                onClick={() => {
                  setIsOpen(false);
                  onViewLoans();
                }}
              >
                <Library className="h-4 w-4 mr-2" />
                My Loans
              </Button>
            )}
            
            {/* My Profile button */}
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              onClick={() => {
                setIsOpen(false);
                setShowProfileDialog(true);
              }}
              data-testid="my-profile-btn"
            >
              <User className="h-4 w-4 mr-2" />
              My Profile
            </Button>

            {/* Membership Card button for all users */}
            <Button
              variant="ghost"
              className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              onClick={() => {
                setIsOpen(false);
                setShowPartnerDialog(true);
              }}
              data-testid="link-partner-btn"
            >
              <Heart className="h-4 w-4 mr-2" />
              Link partner / ACE member
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={() => {
                setIsOpen(false);
                handleDownloadMembershipCard();
              }}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Download Membership Card
            </Button>
            
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              onClick={() => {
                setIsOpen(false);
                onChangePassword && onChangePassword();
              }}
              data-testid="change-password-menu-item"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change password
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              onClick={() => {
                setIsOpen(false);
                onViewPaymentHistory && onViewPaymentHistory();
              }}
              data-testid="payment-history-menu-item"
            >
              <Receipt className="h-4 w-4 mr-2" />
              Payment history
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              onClick={() => {
                setIsOpen(false);
                onExportMyData && onExportMyData();
              }}
              data-testid="export-my-data-menu-item"
            >
              <Download className="h-4 w-4 mr-2" />
              Export my data
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-rose-700 hover:text-rose-800 hover:bg-rose-50"
              onClick={() => {
                setIsOpen(false);
                onDeleteMyAccount && onDeleteMyAccount();
              }}
              data-testid="delete-account-menu-item"
            >
              <X className="h-4 w-4 mr-2" />
              Delete my account
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      )}
      
      {/* Overlay to close menu */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Partner Link Dialog */}
      <PartnerLinkDialog
        open={showPartnerDialog}
        onClose={() => setShowPartnerDialog(false)}
        currentUser={currentUser}
      />

      {/* Profile Dialog */}
      <ProfileDialog
        open={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
        currentUser={currentUser}
        nationalities={nationalities}
      />
    </div>
  );
};

// Partner Link Dialog Component
const PartnerLinkDialog = ({ open, onClose, currentUser }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partner, setPartner] = useState(null);
  const [fetchingPartner, setFetchingPartner] = useState(false);

  // Fetch existing partner on open
  React.useEffect(() => {
    if (!open) return;
    const fetchPartner = async () => {
      try {
        setFetchingPartner(true);
        const res = await axios.get('/users/my-partner');
        if (res.data?.partner) {
          setPartner(res.data.partner);
        } else {
          setPartner(null);
        }
      } catch {
        setPartner(null);
      } finally {
        setFetchingPartner(false);
      }
    };
    fetchPartner();
    setQuery("");
    setResults([]);
    setSelected(null);
    setChecked(false);
  }, [open]);

  const looksLikeEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());
  const [checked, setChecked] = useState(false);

  const handleCheck = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!looksLikeEmail(q)) {
      toast.error("Please enter your partner's complete email address");
      return;
    }
    setSearching(true);
    setSelected(null);
    try {
      const res = await axios.get('/users/partner-search', { params: { q } });
      const found = res.data?.results || [];
      setResults(found);
      if (found.length === 1 && !found[0].already_linked) setSelected(found[0]);
    } catch (err) {
      setResults([]);
      toast.error(err.response?.data?.detail || "Could not check this email");
    } finally {
      setSearching(false);
      setChecked(true);
    }
  };

  const handleLink = async () => {
    if (!selected) {
      toast.error("Check your partner's email address first");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.put('/users/me/partner', { partner_user_id: selected.id });
      setPartner(res.data.partner);
      setQuery("");
      setResults([]);
      setSelected(null);
      toast.success(`Partner linked: ${res.data.partner.first_name} ${res.data.partner.last_name}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to link partner";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async () => {
    try {
      setLoading(true);
      await axios.delete('/users/me/partner');
      setPartner(null);
      toast.success("Partner unlinked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlink partner");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="partner-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-purple-600" />
            Link Partner / ACE Member
          </DialogTitle>
        </DialogHeader>

        {fetchingPartner ? (
          <div className="py-6 text-center text-sm text-slate-500">Loading...</div>
        ) : partner ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="linked-partner-info">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="h-4 w-4 text-green-700" />
                <span className="text-sm font-semibold text-green-900">Currently Linked</span>
              </div>
              <p className="text-sm text-green-800 font-medium">
                {partner.first_name || partner.first_name_1} {partner.last_name}
              </p>
              <p className="text-xs text-green-700">{partner.email}</p>
            </div>
            <p className="text-xs text-slate-500">
              You can book event tickets for this family member from the event booking form.
            </p>
            {currentUser?.role === 'Administrator' ? (
              <Button
                variant="outline"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={handleUnlink}
                disabled={loading}
                data-testid="unlink-partner-btn"
              >
                <X className="h-4 w-4 mr-2" />
                {loading ? 'Unlinking...' : 'Unlink Partner'}
              </Button>
            ) : (
              <p className="text-xs text-slate-400 italic text-center">
                Contact an administrator to unlink your partner.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800" data-testid="partner-link-instructions">
              Enter the complete email address your partner uses for their ACE account. Only one ACE member can be linked at a time.
            </div>
            <form onSubmit={handleCheck}>
              <Label htmlFor="partnerSearch">Partner's email address</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="partnerSearch"
                  type="email"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSelected(null); setResults([]); setChecked(false); }}
                  placeholder="name@example.com"
                  autoComplete="off"
                  data-testid="partner-search-input"
                  autoFocus
                />
                <Button type="submit" variant="outline" disabled={searching || !query.trim()} data-testid="partner-check-btn">
                  {searching ? "Checking..." : "Check"}
                </Button>
              </div>
            </form>

            {checked && !searching && (
              <div data-testid="partner-search-results">
                {results.length === 0 ? (
                  <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 text-center" data-testid="partner-not-found">
                    No ACE member is registered with this email address. Please check it with your partner.
                  </p>
                ) : results[0].already_linked ? (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 text-center" data-testid="partner-already-linked">
                    {results[0].first_name} {results[0].last_name} is already linked to another partner.
                  </p>
                ) : (
                  <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm text-purple-900" data-testid="partner-selected-summary">
                    <span className="font-semibold">Member found:</span> {results[0].first_name} {results[0].last_name}
                  </div>
                )}
              </div>
            )}

            <Button
              type="button"
              onClick={handleLink}
              className="w-full"
              disabled={loading || !selected}
              data-testid="link-partner-submit"
            >
              <Link2 className="h-4 w-4 mr-2" />
              {loading ? 'Linking...' : 'Link Partner / ACE Member'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Forgot Password Form Component

// ── Change Password Form ────────────────────────────────────────────────
// Opened from the profile menu for a signed-in user. Calls the backend
// endpoint `/api/auth/change-password` which verifies the current password,
// enforces the strength rules (same as /auth/reset-password), and rotates
// the bcrypt hash. Existing JWTs stay valid until natural expiry.
export const ChangePasswordForm = ({ isOpen, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientError, setClientError] = useState("");

  const specialChars = "!@#$%^&*()_+-=[]{}|;:,.<>?";
  const rules = [
    { label: "At least 8 characters", pass: newPassword.length >= 8 },
    { label: "One uppercase letter", pass: /[A-Z]/.test(newPassword) },
    { label: "One lowercase letter", pass: /[a-z]/.test(newPassword) },
    { label: "One number", pass: /[0-9]/.test(newPassword) },
    { label: `One special char (${specialChars})`, pass: [...newPassword].some((c) => specialChars.includes(c)) },
    { label: "Different from current password", pass: newPassword.length > 0 && newPassword !== currentPassword },
    { label: "Confirmation matches", pass: newPassword.length > 0 && newPassword === confirmPassword },
  ];
  const allPass = rules.every((r) => r.pass);

  const handleClose = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setLoading(false);
    setClientError("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setClientError("");
    if (!allPass) {
      setClientError("Please satisfy every rule below before saving.");
      return;
    }
    setLoading(true);
    try {
      await axios.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated. Please use the new password next time you sign in.");
      handleClose();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to change password.";
      setClientError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Change password
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-sm text-slate-600">
            Rotate the password on your own account. You'll stay signed in — the new password takes effect on your next sign-in.
          </div>

          <div>
            <Label htmlFor="current-password">Current password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="current-password"
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="Enter your current password"
                required
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                aria-label={showCurrent ? "Hide password" : "Show password"}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="new-password">New password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="Pick a strong new password"
                required
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="confirm-password"
                type={showNew ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10"
                placeholder="Type the new password again"
                required
                data-testid="confirm-password-input"
              />
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
              Password rules
            </p>
            <ul className="space-y-1" data-testid="password-rules-list">
              {rules.map((r) => (
                <li key={r.label} className={`flex items-start gap-2 text-xs ${r.pass ? "text-emerald-700" : "text-slate-500"}`}>
                  <span className={`mt-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] font-bold ${r.pass ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                    {r.pass ? "✓" : ""}
                  </span>
                  {r.label}
                </li>
              ))}
            </ul>
          </div>

          {clientError && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700" data-testid="change-password-error">
              {clientError}
            </div>
          )}

          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-slate-700 hover:bg-slate-800 text-white"
              disabled={loading || !allPass}
              data-testid="change-password-submit"
            >
              {loading ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ── Request Account Deletion Form ───────────────────────────────────────
// A member no longer self-deletes. Clicking "Delete my account" now sends
// a request email to the admin, who is expected to contact the member and
// (if appropriate) delete the account manually from the Users admin page.
export const DeleteAccountForm = ({ isOpen, onClose, onDeleted }) => {
  const [reason, setReason] = useState("");
  const [understand, setUnderstand] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    setReason("");
    setUnderstand(false);
    setLoading(false);
    setError("");
    setSubmitted(false);
    onClose();
  };

  const canSubmit = understand;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await axios.post("/auth/request-account-deletion", { reason: reason.trim() });
      toast.success("Your request has been sent to the administrator.");
      setSubmitted(true);
      // Keep the parent's `onDeleted` unwired here — the member is NOT
      // signed out because no deletion has happened yet.
      if (typeof onDeleted === "function") {
        // Kept for backwards compatibility with callers passing this prop;
        // no-op unless a caller opts into an explicit action after send.
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send request. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <X className="h-5 w-5" />
            Request account deletion
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="space-y-4" data-testid="delete-account-submitted">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 space-y-1">
              <p className="font-medium">Request sent.</p>
              <p className="text-xs">
                The administrator has been notified and will contact you at your registered email
                before any change is made to your account. You can keep using the platform in the
                meantime.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={handleClose}
                className="bg-slate-800 hover:bg-slate-900 text-white"
                data-testid="delete-account-close-after-submit"
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 space-y-2">
              <p className="font-medium">This will send a deletion request to the administrator.</p>
              <p className="text-xs">
                Nothing will be deleted right away. An administrator will get in touch with you at
                your registered email to confirm your request before removing your account.
              </p>
            </div>

            <div>
              <Label htmlFor="delete-reason">
                Reason <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <textarea
                id="delete-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                maxLength={1000}
                className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300"
                placeholder="Tell the admin why you'd like your account removed (optional)"
                data-testid="delete-reason-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {reason.length}/1000 characters
              </p>
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={understand}
                onChange={(e) => setUnderstand(e.target.checked)}
                className="mt-0.5"
                data-testid="delete-understand-checkbox"
              />
              <span>
                I understand this only sends a request — the administrator will contact me before
                deleting my account.
              </span>
            </label>

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700" data-testid="delete-account-error">
                {error}
              </div>
            )}

            <DialogFooter className="pt-1">
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={loading || !canSubmit}
                data-testid="delete-account-submit"
              >
                {loading ? "Sending…" : "Send deletion request"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};




export const ForgotPasswordForm = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post('/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success("Password reset instructions sent! Check your email.");
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send password reset email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setSubmitted(false);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Reset Your Password
          </DialogTitle>
        </DialogHeader>
        
        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-sm text-slate-600">
              Enter your email address and we'll send you a link to reset your password.
            </div>
            
            <div>
              <Label htmlFor="reset-email">Email Address</Label>
              <div className="relative">
                <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Security Information</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    The reset link will expire in 1 hour for security reasons.
                    <br />Administrator will be notified of this request.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-slate-700 hover:bg-slate-800" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-2 text-lg font-medium text-slate-900">Check Your Email</h3>
              <p className="mt-2 text-sm text-slate-600">
                We've sent password reset instructions to <strong>{email}</strong>
              </p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Important:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Check your spam/junk folder if you don't see the email</li>
                  <li>The reset link expires in 1 hour</li>
                  <li>You can request a new link if this one expires</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setEmail("");
                }}
                variant="outline"
                className="flex-1"
              >
                Send Another
              </Button>
              <Button onClick={handleClose} className="flex-1 bg-slate-700 hover:bg-slate-800">
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Reset Password Form Component
export const ResetPasswordForm = ({ isOpen, onClose, token }) => {
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userEmail, setUserEmail] = useState("");

  // Validate token when component opens
  React.useEffect(() => {
    if (isOpen && token) {
      validateToken();
    }
  }, [isOpen, token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`/auth/validate-reset-token?token=${token}`);
      setTokenValid(response.data.valid);
      if (response.data.valid) {
        setUserEmail(response.data.email);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("At least 8 characters long");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("One uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("One lowercase letter");
    }
    if (!/\d/.test(password)) {
      errors.push("One number");
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      errors.push("One special character");
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    const validationErrors = validatePassword(passwords.newPassword);
    if (validationErrors.length > 0) {
      toast.error(`Password must have: ${validationErrors.join(", ")}`);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post('/auth/reset-password', {
        token: token,
        new_password: passwords.newPassword
      });
      
      toast.success("Password reset successfully! You can now login with your new password.");
      handleClose();
      
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPasswords({ newPassword: "", confirmPassword: "" });
    setShowPasswords({ newPassword: false, confirmPassword: false });
    setLoading(false);
    setTokenValid(null);
    setUserEmail("");
    onClose();
  };

  if (tokenValid === null) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700 mx-auto"></div>
            <p className="mt-4 text-slate-600">Validating reset token...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!tokenValid) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="mt-2 text-lg font-medium text-slate-900">Invalid Reset Link</h3>
            <p className="mt-2 text-sm text-slate-600">
              This password reset link is invalid or has expired.
            </p>
            <Button onClick={handleClose} className="mt-4 bg-slate-700 hover:bg-slate-800">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const passwordErrors = validatePassword(passwords.newPassword);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Set New Password
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-sm text-slate-600">
            Creating new password for: <strong>{userEmail}</strong>
          </div>
          
          <div>
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="new-password"
                type={showPasswords.newPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords({...passwords, newPassword: e.target.value})}
                className="pl-10 pr-10"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1 h-8 w-8"
                onClick={() => setShowPasswords({...showPasswords, newPassword: !showPasswords.newPassword})}
              >
                {showPasswords.newPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            
            {passwords.newPassword && passwordErrors.length > 0 && (
              <div className="mt-2 text-sm text-red-600">
                <p className="font-medium">Password must have:</p>
                <ul className="list-disc list-inside mt-1">
                  {passwordErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <div className="relative">
              <Lock className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                id="confirm-password"
                type={showPasswords.confirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({...passwords, confirmPassword: e.target.value})}
                className="pl-10 pr-10"
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1 h-8 w-8"
                onClick={() => setShowPasswords({...showPasswords, confirmPassword: !showPasswords.confirmPassword})}
              >
                {showPasswords.confirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            
            {passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword && (
              <div className="mt-2 text-sm text-red-600">
                Passwords do not match
              </div>
            )}
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-900">Password Requirements</h4>
                <ul className="text-sm text-green-700 mt-1 list-disc list-inside">
                  <li>At least 8 characters long</li>
                  <li>Include uppercase and lowercase letters</li>
                  <li>Include at least one number</li>
                  <li>Include at least one special character</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button 
              type="submit" 
              className="flex-1 bg-slate-700 hover:bg-slate-800" 
              disabled={loading || passwordErrors.length > 0 || passwords.newPassword !== passwords.confirmPassword}
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Profile Dialog Component
const ProfileDialog = ({ open, onClose, currentUser, nationalities = [] }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [correctionField, setCorrectionField] = useState(null); // {field, label}
  const [correctionValue, setCorrectionValue] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);

  React.useEffect(() => {
    if (open && currentUser) {
      setForm({
        first_name_1: currentUser.first_name_1 || '',
        first_name_2: currentUser.first_name_2 || '',
        last_name: currentUser.last_name || '',
        mobile: currentUser.mobile || '',
        landline: currentUser.landline || '',
        birth_date: currentUser.birth_date || '',
        birth_place: currentUser.birth_place || '',
        nationality: currentUser.nationality || '',
        other_nationality: currentUser.other_nationality || '',
        address: currentUser.address || '',
        postal_code: currentUser.postal_code || '',
        city: currentUser.city || '',
        province: currentUser.province || '',
        country: currentUser.country || 'Italy',
        fiscal_code: currentUser.fiscal_code || '',
      });
      setEditing(false);
    }
  }, [open, currentUser]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/auth/profile', form);
      toast.success('Profile updated successfully');
      setEditing(false);
      onClose();
      // Reload page to refresh currentUser
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const submitCorrectionRequest = async () => {
    if (!correctionField) return;
    if (!correctionValue.trim()) {
      toast.error('Please enter the corrected value');
      return;
    }
    if (correctionReason.trim().length < 5) {
      toast.error('Please briefly explain the reason (5+ characters)');
      return;
    }
    setCorrectionSubmitting(true);
    try {
      const res = await axios.post('/auth/profile/correction-request', {
        field: correctionField.field,
        requested_value: correctionValue.trim(),
        reason: correctionReason.trim()
      });
      toast.success(res.data?.message || 'Correction request sent', { duration: 6000 });
      setCorrectionField(null);
      setCorrectionValue('');
      setCorrectionReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send correction request');
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            My Profile
            {currentUser?.member_number && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">#{currentUser.member_number}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email (non-editable) */}
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1 flex items-center justify-between gap-2">
              <span>Email <span className="text-slate-400 font-normal">(not editable)</span></span>
              <button
                type="button"
                className="text-[11px] text-teal-700 hover:text-teal-900 hover:underline font-medium"
                onClick={() => { setCorrectionField({ field: 'email', label: 'Email' }); setCorrectionValue(currentUser?.email || ''); setCorrectionReason(''); }}
                data-testid="request-correction-email"
              >
                Request correction
              </button>
            </label>
            <p className="text-sm text-slate-600 py-1.5 px-3 bg-slate-100 rounded border">
              {currentUser?.email}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: "First Name", field: "first_name_1", locked: true },
              { label: "Second Name", field: "first_name_2" },
              { label: "Last Name", field: "last_name", locked: true },
              { label: "Birth Date", field: "birth_date", locked: true },
              { label: "Birth Place", field: "birth_place" },
              { label: "Nationality", field: "nationality", isSelect: true },
              { label: "Other Nationality", field: "other_nationality" },
              { label: "Fiscal Code", field: "fiscal_code" },
              { label: "Mobile", field: "mobile" },
              { label: "Landline", field: "landline" },
              { label: "Address", field: "address" },
              { label: "Postal Code", field: "postal_code" },
              { label: "City", field: "city" },
              { label: "Province", field: "province" },
              { label: "Country", field: "country" },
            ].map(({ label, field, isSelect, locked }) => (
              <div key={field}>
                <label className="text-xs font-medium text-slate-500 block mb-1 flex items-center justify-between gap-2">
                  <span>{label}{locked && <span className="text-slate-400 font-normal"> (not editable)</span>}</span>
                  {locked && (
                    <button
                      type="button"
                      className="text-[11px] text-teal-700 hover:text-teal-900 hover:underline font-medium"
                      onClick={() => { setCorrectionField({ field, label }); setCorrectionValue(form[field] || ''); setCorrectionReason(''); }}
                      data-testid={`request-correction-${field}`}
                    >
                      Request correction
                    </button>
                  )}
                </label>
                {editing && !locked ? (
                  isSelect && nationalities.length > 0 ? (
                    <Select
                      value={form[field] || ''}
                      onValueChange={(value) => setForm(prev => ({ ...prev, [field]: value }))}
                    >
                      <SelectTrigger data-testid={`profile-${field}`}>
                        <SelectValue placeholder="Select nationality" />
                      </SelectTrigger>
                      <SelectContent>
                        {nationalities.map((nat) => (
                          <SelectItem key={nat} value={nat}>{nat}</SelectItem>
                        ))}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type="text"
                      value={form[field] || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                      data-testid={`profile-${field}`}
                    />
                  )
                ) : (
                  <p
                    className={`text-sm py-1.5 px-3 rounded border min-h-[36px] ${locked ? 'text-slate-600 bg-slate-100' : 'text-slate-800 bg-slate-50'}`}
                    data-testid={`profile-view-${field}`}
                  >
                    {form[field] || '—'}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            {!editing ? (
              <>
                <Button variant="outline" onClick={onClose}>Close</Button>
                <Button onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Sub-dialog: Request correction for a locked field */}
      <Dialog open={!!correctionField} onOpenChange={(o) => { if (!o) setCorrectionField(null); }}>
        <DialogContent className="max-w-md" data-testid="profile-correction-dialog">
          <DialogHeader>
            <DialogTitle>Request a correction</DialogTitle>
          </DialogHeader>
          {correctionField && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Your request will be emailed to the secretariat. They'll review and update your profile on your behalf.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Member ID</Label>
                  <p
                    className="text-sm font-medium mt-1 px-3 py-2 bg-slate-100 rounded border"
                    data-testid="correction-member-id"
                  >
                    {currentUser?.member_number ? `#${currentUser.member_number}` : '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Field</Label>
                  <p className="text-sm font-medium mt-1 px-3 py-2 bg-slate-100 rounded border">
                    {correctionField.label}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Existing value</Label>
                <p
                  className="text-sm font-medium mt-1 px-3 py-2 bg-slate-100 rounded border break-words"
                  data-testid="correction-existing-value"
                >
                  {(correctionField.field === 'email'
                    ? currentUser?.email
                    : form[correctionField.field]) || '—'}
                </p>
              </div>
              <div>
                <Label htmlFor="corr-value" className="text-xs text-slate-500">Corrected value *</Label>
                <Input
                  id="corr-value"
                  value={correctionValue}
                  onChange={(e) => setCorrectionValue(e.target.value)}
                  placeholder={correctionField.label === 'Birth Date' ? 'DD/MM/YYYY' : ''}
                  data-testid="correction-requested-value"
                />
              </div>
              <div>
                <Label htmlFor="corr-reason" className="text-xs text-slate-500">Reason *</Label>
                <Textarea
                  id="corr-reason"
                  rows={3}
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  placeholder="e.g. There is a typo in my first name…"
                  data-testid="correction-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionField(null)} disabled={correctionSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={submitCorrectionRequest}
              disabled={correctionSubmitting}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="correction-submit-btn"
            >
              {correctionSubmitting ? 'Sending…' : 'Send request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
